import { useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  FigureFormat,
  FundFigure,
  FundFigureKey,
  FundMetricsPoint,
} from '@/types/fund';
import {
  figureValue,
  formatFigure,
  formatFigureAxis,
  quarterLabel,
  shortQuarterLabel,
} from './figureFormat';

const INK = '#1e293b';
const GRID_COLOR = 'hsl(var(--border))';
const TICK_STYLE = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };
const FILLED_FORMATS: FigureFormat[] = ['amount', 'percent'];

interface ChartPoint {
  asOf: string;
  value: number | null;
}

interface FigureChartProps {
  figures: FundFigure[];
  initialFigure: FundFigureKey;
  points: FundMetricsPoint[];
  currency: string;
}

interface ReadoutTooltipProps {
  active?: boolean;
  payload?: { payload: ChartPoint }[];
  figure: FundFigure;
  currency: string;
}

function ReadoutTooltip({
  active,
  payload,
  figure,
  currency,
}: ReadoutTooltipProps) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;

  return (
    <div className="rounded-md border bg-background/95 px-3 py-2 text-xs shadow-sm tabular-nums">
      <div className="text-muted-foreground">{quarterLabel(point.asOf)}</div>
      <div className="font-semibold">
        {formatFigure(point.value, figure.format, currency)}
      </div>
    </div>
  );
}

function yDomain(format: FigureFormat) {
  const lower = (min: number) => Math.min(0, min);
  const upper = (max: number) =>
    format === 'multiple' ? Math.max(1, max) : max;
  return [lower, upper] as [(min: number) => number, (max: number) => number];
}

export function FigureChart({
  figures,
  initialFigure,
  points,
  currency,
}: FigureChartProps) {
  const [figureKey, setFigureKey] = useState(initialFigure);
  const figure = figures.find((f) => f.figure === figureKey);
  if (!figure) return null;

  const options = figures.filter((f) => f.format === figure.format);
  const data: ChartPoint[] = points.map((point) => ({
    asOf: point.as_of,
    value: figureValue(point, figure.figure),
  }));
  const latest = data.at(-1);
  const filled = FILLED_FORMATS.includes(figure.format);

  return (
    <div
      className="min-w-0 rounded-lg border bg-card p-5 shadow-sm"
      data-testid="figure-chart"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Select
            value={figure.figure}
            onValueChange={(value) => setFigureKey(value as FundFigureKey)}
          >
            <SelectTrigger
              aria-label="Figure"
              className="h-auto w-auto gap-1.5 border-none p-0 text-sm font-semibold shadow-none focus:ring-0"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.figure} value={option.figure}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Quarterly{figure.format === 'amount' ? `, ${currency}` : ''}
          </div>
        </div>
        {latest && (
          <div className="text-right">
            <div className="text-[15px] font-semibold tabular-nums">
              {formatFigure(latest.value, figure.format, currency)}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {quarterLabel(latest.asOf)} · latest
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 h-[190px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID_COLOR} />
            <XAxis
              dataKey="asOf"
              tickFormatter={shortQuarterLabel}
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={false}
              minTickGap={24}
            />
            <YAxis
              domain={yDomain(figure.format)}
              tickFormatter={(value: number) =>
                formatFigureAxis(value, figure.format, currency)
              }
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={false}
              tickCount={5}
              width={56}
            />
            {figure.format === 'multiple' && (
              <ReferenceLine
                y={1}
                stroke="hsl(var(--muted-foreground))"
                strokeDasharray="4 3"
              />
            )}
            <Tooltip
              content={<ReadoutTooltip figure={figure} currency={currency} />}
              cursor={{ stroke: 'hsl(var(--muted-foreground))' }}
            />
            <Area
              dataKey="value"
              type="linear"
              stroke={INK}
              strokeWidth={1.75}
              fill={filled ? INK : 'none'}
              fillOpacity={0.06}
              dot={false}
              activeDot={{ r: 3.5, fill: INK }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
